import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Link, MessagePlugin, Popconfirm, Space, Tooltip, Upload } from 'tdesign-react';
import { useEventEmitter, useMemoizedFn } from 'ahooks';
import { produce } from 'immer';
import { useNavigate } from 'react-router-dom';
import { downloadFile } from '@/utils/common';
import { deleteScript, scriptExport, uploadScriptFile } from '@/services/templateService';
import ListPanel from '@/components/ListPanel';
import PermWrapper from '@/components/PermWrapper';
import ScriptTable, { defaultColumns } from '../components/ScriptTable';

export default function List() {
  const [selectedRowData, setSelectedRowData] = useState([]);
  const refreshEmmiter = useEventEmitter();
  const navigate = useNavigate();

  const operators = (
    <PermWrapper resource='pulsar:stencil:script:create'>
      <Button
        theme='primary'
        onClick={() => {
          navigate('/maintenance/template/script/detail');
        }}
      >
        创建脚本
      </Button>
    </PermWrapper>
  );

  const columns = produce(defaultColumns, (draft) => {
    draft.push({
      title: '创建人',
      colKey: 'createBy',
      width: 150,
    });
    draft.push({
      title: '最后修改人',
      colKey: 'updateBy',
      width: 150,
    });
    draft.push({
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      cell: ({ row }) => (
        <Space>
          <PermWrapper resource='pulsar:stencil:script:update'>
            <Link
              theme='primary'
              onClick={() => navigate(`/maintenance/template/script/detail/${row.id}`)}
              hover='color'
            >
              编辑
            </Link>
          </PermWrapper>
          {!row.systemScript && (
            <PermWrapper resource='pulsar:stencil:script:delete'>
              <Popconfirm theme='danger' content='确定删除？' onConfirm={() => doDelete(row)}>
                <Link theme='danger' hover='color'>
                  删除
                </Link>
              </Popconfirm>
            </PermWrapper>
          )}
        </Space>
      ),
      width: 120,
    });
  });

  const doRefresh = () => {
    refreshEmmiter.emit();
  };

  const download = async () => {
    doRefresh();
    const { data } = await scriptExport({
      idList: selectedRowData.map((item) => item.id),
    });
    downloadFile(new Blob([data], { type: 'application/json;charset=utf-8' }), '脚本信息_');
  };

  const doUpload = async (file) => {
    if (file[0]) {
      const formData = new FormData();
      formData.append('file', file[0]);
      let uploadMsg;
      try {
        uploadMsg = MessagePlugin.loading({
          duration: 0,
          content: '正在上传...',
        });
        await uploadScriptFile(formData);
        MessagePlugin.success('上传成功！');
        doRefresh();
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        MessagePlugin.close(uploadMsg);
      }
    }
  };

  const onSelectChange = (value, { selectedRowData }) => {
    setSelectedRowData(selectedRowData);
  };

  const doDelete = useMemoizedFn(async ({ id }) => {
    try {
      await deleteScript(id);
      MessagePlugin.success('删除成功');
      doRefresh();
    } catch (error) {
      console.trace(error);
      throw error;
    }
  });

  const afterLoadData = (datas) => {
    setSelectedRowData([]);
    return datas;
  };

  return (
    <ListPanel
      header='脚本管理'
      tip='通用脚本的模板， 脚本模板可以在脚本执行及流程执行阶段引用使用。'
      operators={
        <Space>
          <PermWrapper resource='pulsar:stencil:script:export'>
            <Tooltip content={isEmpty(selectedRowData) ? '请先勾选要导出的脚本' : null}>
              <BigButton
                theme='default'
                variant='outline'
                onClick={download}
                size='large'
                disabled={isEmpty(selectedRowData)}
              >
                批量导出
              </BigButton>
            </Tooltip>
          </PermWrapper>
          <PermWrapper resource='pulsar:stencil:script:import'>
            <Upload theme='file' onSelectChange={doUpload}>
              <BigButton theme='default' variant='outline' size='large'>
                导入JSON文件
              </BigButton>
            </Upload>
          </PermWrapper>
        </Space>
      }
    >
      <ScriptTable
        selectedRowData={selectedRowData}
        onSelectChange={onSelectChange}
        refreshEmmiter={refreshEmmiter}
        columns={columns}
        operators={operators}
        afterLoadData={afterLoadData}
      />
    </ListPanel>
  );
}

const BigButton = styled(Button)`
  border: none;
  &&& {
    font: var(--td-font-body-medium);
  }
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1), 0px 1px 2px -1px rgba(0, 0, 0, 0.1);
`;
